import React, { useState, useEffect, JSX } from "react"
import { useLocation } from "react-router-dom"
import PropTypes from "prop-types"
import AppBar from "@mui/material/AppBar"
import Toolbar from "@mui/material/Toolbar"
import IconButton from "@mui/material/IconButton"
import Menu from "@mui/material/Menu"
import Icon from "@mui/material/Icon"
import MDBox from "components/MDBox"
import Breadcrumbs from "../Breadcrumbs"
import NotificationItem from "pages/components/NotificationItem"
import ManageUserName from "pages/manage/username"
import ManagePassword from "pages/manage/password"
import { manageLogout } from "../../../api/manage_logout"
import { useNavigate } from "react-router-dom"

// Custom styles for DashboardNavbar
import { navbar, navbarContainer, navbarRow, navbarIconButton, navbarMobileMenu } from "./styles"

// Material Dashboard 2 React context
import { useMaterialUIController, setTransparentNavbar, setMiniSidenav } from "context"

interface Props {
  absolute: boolean
  light: boolean
  isMini: boolean
}

export default function DashboardNavbar({ absolute, light, isMini }: Props): JSX.Element {
  const [navbarType, setNavbarType] = useState<"fixed" | "absolute" | "relative" | "static" | undefined>()
  const [controller, dispatch] = useMaterialUIController()
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller
  const [openMenu, setOpenMenu] = useState<any>(false)
  const route = useLocation().pathname.split("/").slice(1)
  const [showUserName, setShowUserName] = useState<string>("")
  const [profileName, setProfileName] = useState<boolean>(false)
  const [profilePassword, setProfilePassword] = useState<boolean>(false)
  const navigate = useNavigate()

  useEffect(() => {
    let name = localStorage.getItem("name")
    if (name) {
      const auth = localStorage.getItem("account_type")
      if (auth === "service") {
        const catvName = localStorage.getItem("show_mode_catv_name")
        if (catvName !== null && catvName != "" && catvName != "-") {
          name = name + "(" + catvName + ")"
        }
        const estateName = localStorage.getItem("show_mode_estate_name")
        if (estateName !== null && estateName != "" && estateName != "-") {
          name = name + "(" + estateName + ")"
        }
      }
      setShowUserName(name)
    }
  })

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("fixed")
    } else {
      setNavbarType("absolute")
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar)
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar)

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar()

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar)
  }, [dispatch, fixedNavbar])

  // プロフィール編集
  const handleProfileName = () => {
    handleCloseMenu()
    setProfileName(true)
  }

  const handleProfileNameClose = (name: string = "") => {
    //console.log(name);
    setProfileName(false)
    if (name != "") {
      localStorage.setItem("name", name)
      setShowUserName(name)
    }
  }

  // パスワード変更
  const handlePassword = () => {
    handleCloseMenu()
    setProfilePassword(true)
  }

  const handlePasswordClose = async (num: number) => {
    setProfilePassword(false)
    if (num == 1) {
      // ログイン画面に戻る
      localStorage.setItem("session_code", "")
      navigate("/login")
    }
  }

  // ログアウト
  const handleLogout = async () => {
    // ログイン画面に戻る
    try {
      await manageLogout()
    } catch (e) {
      console.log(e)
    }
    localStorage.setItem("session_code", "")
    navigate("/login")
  }

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav)
  const handleOpenMenu = (event: any) => setOpenMenu(event.currentTarget)
  const handleCloseMenu = () => setOpenMenu(false)

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference="anchorEl"
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left"
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem icon={<Icon>person</Icon>} title="名前変更" onClick={handleProfileName} />
      <NotificationItem icon={<Icon>key</Icon>} title="パスワード変更" onClick={handlePassword} />
      <NotificationItem icon={<Icon>logout</Icon>} title="ログアウト" onClick={handleLogout} />
    </Menu>
  )

  // Styles for the navbar icons
  const iconsStyle = ({ palette: { dark, white, text }, functions: { rgba } }: any) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main
      }

      return colorValue
    }
  })

  return (
    <AppBar
      position={absolute ? "absolute" : navbarType}
      color="inherit"
      sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
    >
      {profileName && <ManageUserName name={showUserName} onClose={handleProfileNameClose} />}
      {profilePassword && <ManagePassword onClose={handlePasswordClose} />}
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme: any) => navbarRow(theme, { isMini })}>
          <Breadcrumbs icon="home" title={route[route.length - 1]} route={route} light={light} />
        </MDBox>
        {isMini ? null : (
          <MDBox sx={(theme: any) => navbarRow(theme, { isMini })}>
            <MDBox color={light ? "white" : "inherit"}>
              <IconButton
                size="small"
                disableRipple
                color="inherit"
                sx={navbarIconButton}
                aria-controls="notification-menu"
                aria-haspopup="true"
                onClick={handleOpenMenu}
                style={{ textAlign: "left" }}
              >
                {showUserName}
                <span style={{ width: "10px" }} />
                <Icon sx={iconsStyle}>settings</Icon>
              </IconButton>
              <IconButton size="small" disableRipple color="inherit" sx={navbarMobileMenu} onClick={handleMiniSidenav}>
                <Icon sx={iconsStyle} fontSize="medium">
                  {miniSidenav ? "menu" : "menu_open"}
                </Icon>
              </IconButton>
              <span style={{ width: "20" }} />
              {renderMenu()}
            </MDBox>
          </MDBox>
        )}
      </Toolbar>
    </AppBar>
  )
}

// Setting default values for the props of DashboardNavbar
DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false
}

// Typechecking props for the DashboardNavbar
DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool
}
